import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { server } from "../server";

const UpdateMockTestSeries = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const mockTestSeriesId = location.state?.mockTestSeriesId;

  const [testData, setTestData] = useState({
    title: "",
    description: "",
    subjects: [],
    exams: [],
    testType: "Full",
    difficulty: "Medium",
    totalQuestions: 0,
    totalMarks: 0,
    duration: 90,
    questionIds: [],
    randomizeQuestions: true,
    correctMark: 4,
    wrongMark: -1,
    hasNegativeMarking: true,
    status: "Draft",
    isActive: true,
    attemptsAllowed: 3,
    startDate: "",
    endDate: "",
  });

  const [subjects, setSubjects] = useState([]);
  const [allQuestions, setAllQuestions] = useState([]);
  const [availableQuestions, setAvailableQuestions] = useState([]);
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedSubject, setSelectedSubject] = useState("");
  const [activeTab, setActiveTab] = useState("config");

  console.log(location.state);

  // Fetch test series data and related data
  useEffect(() => {
    if (mockTestSeriesId) {
      fetchTestSeriesData();
    } else {
      alert("No test series ID provided");
      navigate("/updateMockTestSeries");
    }
  }, [mockTestSeriesId, navigate]);

  const fetchTestSeriesData = async () => {
    try {
      setLoading(true);

      const [testResponse, subjectsResponse, questionsResponse] =
        await Promise.all([
          axios.get(`${server}/mockTestSeries/getById`, {
            params: { testSeriesId: mockTestSeriesId },
          }),
          axios.get(`${server}/subject/all`, {
            params: { pageNo: 1, pageSize: 100 },
          }),
          axios.get(`${server}/question/all`, {
            params: { pageNo: 1, pageSize: 500 },
          }),
        ]);

      if (testResponse.data?.success) {
        const test = testResponse.data.data.mockTestSeriesDetails;
        const questions = testResponse.data.data.questions || [];

        setTestData({
          title: test.title || "",
          description: test.description || "",
          subjects: test.subjects || [],
          exams: test.exams || [],
          testType: test.testType || "Full",
          difficulty: test.difficulty || "Medium",
          totalQuestions: test.totalQuestions || 0,
          totalMarks: test.totalMarks || 0,
          duration: test.duration || 90,
          questionIds: test.questionIds || [],
          randomizeQuestions: test.randomizeQuestions ?? true,
          correctMark: test.correctMark || 4,
          wrongMark: test.wrongMark || -1,
          hasNegativeMarking: test.hasNegativeMarking ?? true,
          status: test.status || "Draft",
          isActive: test.isActive ?? true,
          attemptsAllowed: test.attemptsAllowed || 3,
          startDate: test.startDate ? test.startDate.split("T")[0] : "",
          endDate: test.endDate ? test.endDate.split("T")[0] : "",
        });

        // Set selected questions from the API response
        setSelectedQuestions(questions);
      }

      if (subjectsResponse.data?.data) {
        setSubjects(subjectsResponse.data.data);
      }

      if (questionsResponse.data?.data) {
        setAllQuestions(questionsResponse.data.data);
      }
    } catch (error) {
      console.error("Error fetching test series data:", error);
      alert("Failed to load test series data");
    } finally {
      setLoading(false);
    }
  };

  // Filter available questions
  useEffect(() => {
    let filtered = allQuestions.filter(
      (q) => !selectedQuestions.find((sq) => sq.id === q.id)
    );

    if (searchQuery) {
      filtered = filtered.filter(
        (q) =>
          q.question?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          q.topic?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          q.subject?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    if (selectedSubject) {
      filtered = filtered.filter((q) => q.subject === selectedSubject);
    }

    setAvailableQuestions(filtered);
  }, [searchQuery, selectedSubject, allQuestions, selectedQuestions]);

  // Update test data when selected questions change
  useEffect(() => {
    const questionIds = selectedQuestions.map((q) => q.id);
    const totalMarks = selectedQuestions.length * testData.correctMark;

    setTestData((prev) => ({
      ...prev,
      questionIds,
      totalQuestions: selectedQuestions.length,
      totalMarks,
    }));
  }, [selectedQuestions, testData.correctMark]);

  // Add question to selected
  const addQuestion = (question) => {
    setSelectedQuestions((prev) => [...prev, question]);
  };

  // Remove question from selected
  const removeQuestion = (questionId) => {
    setSelectedQuestions((prev) => prev.filter((q) => q.id !== questionId));
  };

  // Move question up/down
  const moveQuestion = (index, direction) => {
    const newSelected = [...selectedQuestions];
    if (direction === "up" && index > 0) {
      [newSelected[index], newSelected[index - 1]] = [
        newSelected[index - 1],
        newSelected[index],
      ];
    } else if (direction === "down" && index < newSelected.length - 1) {
      [newSelected[index], newSelected[index + 1]] = [
        newSelected[index + 1],
        newSelected[index],
      ];
    }
    setSelectedQuestions(newSelected);
  };

  // Update test series
  const updateTestSeries = async () => {
    try {
      if (!testData.title.trim()) {
        alert("Please enter test title");
        return;
      }

      if (selectedQuestions.length === 0) {
        alert("Please select at least one question");
        return;
      }

      setSaving(true);

      const payload = {
        title: testData.title,
        description: testData.description,
        subjects: testData.subjects,
        exams: testData.exams,
        testType: testData.testType,
        difficulty: testData.difficulty,
        duration: testData.duration,
        questionIds: testData.questionIds,
        randomizeQuestions: testData.randomizeQuestions,
        correctMark: testData.correctMark,
        wrongMark: testData.wrongMark,
        hasNegativeMarking: testData.hasNegativeMarking,
        status: testData.status,
        isActive: testData.isActive,
        attemptsAllowed: testData.attemptsAllowed,
        startDate: testData.startDate
          ? `${testData.startDate}T00:00:00.000Z`
          : null,
        endDate: testData.endDate ? `${testData.endDate}T23:59:59.999Z` : null,
      };

      const response = await axios.put(
        `${server}/mockTestSeries/updateById`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          params: {
            testSeriesId: mockTestSeriesId,
          },
        }
      );

      if (response.data.success) {
        alert("Test series updated successfully!");
        navigate("../mockTestSeries");
      }
    } catch (error) {
      console.error("Error updating test series:", error);
      alert(error.response?.data?.message || "Failed to update test series");
    } finally {
      setSaving(false);
    }
  };

  const testTypes = [
    { value: "Full", label: "Full Length", icon: "📊" },
    { value: "Subject", label: "Subject Wise", icon: "📚" },
    { value: "Chapter", label: "Chapter Wise", icon: "📖" },
    { value: "Topic", label: "Topic Wise", icon: "🎯" },
  ];

  const difficulties = [
    { value: "Easy", label: "Easy", color: "bg-green-500" },
    { value: "Medium", label: "Medium", color: "bg-yellow-500" },
    { value: "Hard", label: "Hard", color: "bg-red-500" },
    { value: "Mixed", label: "Mixed", color: "bg-purple-500" },
  ];

  const exams = [
    "JEE Main",
    "JEE Advanced",
    "NEET",
    "UPSC",
    "SSC",
    "Bank PO",
    "GATE",
    "CAT",
  ];

  const statusOptions = [
    { value: "Draft", label: "Draft" },
    { value: "Published", label: "Published" },
    { value: "Archived", label: "Archived" },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white text-lg">Loading test series data...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Header */}
      <div className="border-b border-gray-700 px-6 py-4 bg-gray-800">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Update Test Series</h1>
            <p className="text-gray-400 text-sm mt-1">
              Edit and configure your mock test series
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/mockTestSeries")}
              className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg font-medium transition-colors"
            >
              Back to List
            </button>
            <button
              onClick={updateTestSeries}
              disabled={saving}
              className="px-6 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 rounded-lg font-medium transition-colors flex items-center gap-2"
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Updating...
                </>
              ) : (
                "Update Test Series"
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-700 px-6 bg-gray-800">
        <div className="flex gap-4">
          <button
            onClick={() => setActiveTab("config")}
            className={`py-4 px-2 border-b-2 font-medium transition-colors ${
              activeTab === "config"
                ? "border-blue-500 text-blue-400"
                : "border-transparent text-gray-400 hover:text-gray-300"
            }`}
          >
            Configuration
          </button>
          <button
            onClick={() => setActiveTab("questions")}
            className={`py-4 px-2 border-b-2 font-medium transition-colors ${
              activeTab === "questions"
                ? "border-blue-500 text-blue-400"
                : "border-transparent text-gray-400 hover:text-gray-300"
            }`}
          >
            Questions ({selectedQuestions.length})
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6">
        {activeTab === "config" ? (
          <div className="space-y-6">
            {/* Basic Information */}
            <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
              <h2 className="text-lg font-semibold mb-4">Basic Information</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Test Title *
                  </label>
                  <input
                    type="text"
                    value={testData.title}
                    onChange={(e) =>
                      setTestData((prev) => ({
                        ...prev,
                        title: e.target.value,
                      }))
                    }
                    className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-blue-500 transition-colors"
                    placeholder="e.g., JEE Main Full Test 1"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Status
                  </label>
                  <select
                    value={testData.status}
                    onChange={(e) =>
                      setTestData((prev) => ({
                        ...prev,
                        status: e.target.value,
                      }))
                    }
                    className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-blue-500 transition-colors"
                  >
                    {statusOptions.map((status) => (
                      <option key={status.value} value={status.value}>
                        {status.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="lg:col-span-2">
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Description
                  </label>
                  <textarea
                    value={testData.description}
                    onChange={(e) =>
                      setTestData((prev) => ({
                        ...prev,
                        description: e.target.value,
                      }))
                    }
                    rows={3}
                    className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-blue-500 transition-colors resize-none"
                    placeholder="Describe the test series..."
                  />
                </div>
              </div>
            </div>

            {/* Test Settings */}
            <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
              <h2 className="text-lg font-semibold mb-4">Test Settings</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-3">
                    Test Type
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    {testTypes.map((type) => (
                      <button
                        key={type.value}
                        onClick={() =>
                          setTestData((prev) => ({
                            ...prev,
                            testType: type.value,
                          }))
                        }
                        className={`p-4 rounded-lg border transition-all ${
                          testData.testType === type.value
                            ? "border-blue-500 bg-blue-500/10"
                            : "border-gray-700 bg-gray-900 hover:border-gray-600"
                        }`}
                      >
                        <div className="text-lg mb-1">{type.icon}</div>
                        <div className="text-sm font-medium">{type.label}</div>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-3">
                    Difficulty Level
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    {difficulties.map((diff) => (
                      <button
                        key={diff.value}
                        onClick={() =>
                          setTestData((prev) => ({
                            ...prev,
                            difficulty: diff.value,
                          }))
                        }
                        className={`p-3 rounded-lg border transition-all ${
                          testData.difficulty === diff.value
                            ? "border-blue-500 bg-blue-500/10"
                            : "border-gray-700 bg-gray-900 hover:border-gray-600"
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <div
                            className={`w-3 h-3 rounded-full ${diff.color}`}
                          ></div>
                          <span className="text-sm font-medium">
                            {diff.label}
                          </span>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Duration (minutes)
                    </label>
                    <input
                      type="number"
                      value={testData.duration}
                      onChange={(e) =>
                        setTestData((prev) => ({
                          ...prev,
                          duration: parseInt(e.target.value) || 180,
                        }))
                      }
                      className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-blue-500 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Attempts Allowed
                    </label>
                    <input
                      type="number"
                      value={testData.attemptsAllowed}
                      onChange={(e) =>
                        setTestData((prev) => ({
                          ...prev,
                          attemptsAllowed: parseInt(e.target.value) || 3,
                        }))
                      }
                      className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-blue-500 transition-colors"
                      min="1"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-3">
                    Target Exams
                  </label>
                  <div className="max-h-40 overflow-y-auto bg-gray-900 border border-gray-700 rounded-lg p-3">
                    <div className="grid grid-cols-2 gap-2">
                      {exams.map((exam) => (
                        <label
                          key={exam}
                          className="flex items-center space-x-2 p-2 rounded hover:bg-gray-800 transition-colors cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={testData.exams.includes(exam)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setTestData((prev) => ({
                                  ...prev,
                                  exams: [...prev.exams, exam],
                                }));
                              } else {
                                setTestData((prev) => ({
                                  ...prev,
                                  exams: prev.exams.filter((e) => e !== exam),
                                }));
                              }
                            }}
                            className="w-4 rounded bg-gray-800 border-gray-600 text-blue-500 focus:ring-blue-500"
                          />
                          <span className="text-sm text-gray-300">{exam}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Scoring & Dates */}
            <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
              <h2 className="text-lg font-semibold mb-4">Scoring & Schedule</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-3">
                    Scoring Pattern
                  </label>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-xs text-gray-400 mb-2">
                        Correct Answer Marks
                      </label>
                      <input
                        type="number"
                        value={testData.correctMark}
                        onChange={(e) =>
                          setTestData((prev) => ({
                            ...prev,
                            correctMark: parseFloat(e.target.value) || 4,
                          }))
                        }
                        className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-green-500 transition-colors"
                        step="0.5"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-2">
                        Wrong Answer Marks
                      </label>
                      <input
                        type="number"
                        value={testData.wrongMark}
                        onChange={(e) =>
                          setTestData((prev) => ({
                            ...prev,
                            wrongMark: parseFloat(e.target.value) || -1,
                          }))
                        }
                        className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-red-500 transition-colors"
                        step="0.5"
                      />
                    </div>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gray-900 rounded-lg border border-gray-700">
                    <div>
                      <span className="text-sm font-medium text-gray-300">
                        Negative Marking
                      </span>
                      <p className="text-xs text-gray-500 mt-1">
                        Enable negative marks for wrong answers
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={testData.hasNegativeMarking}
                        onChange={(e) =>
                          setTestData((prev) => ({
                            ...prev,
                            hasNegativeMarking: e.target.checked,
                          }))
                        }
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-5 peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-3">
                    Schedule (Optional)
                  </label>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-xs text-gray-400 mb-2">
                        Start Date
                      </label>
                      <input
                        type="date"
                        value={testData.startDate}
                        onChange={(e) =>
                          setTestData((prev) => ({
                            ...prev,
                            startDate: e.target.value,
                          }))
                        }
                        className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-blue-500 transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-2">
                        End Date
                      </label>
                      <input
                        type="date"
                        value={testData.endDate}
                        onChange={(e) =>
                          setTestData((prev) => ({
                            ...prev,
                            endDate: e.target.value,
                          }))
                        }
                        className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-blue-500 transition-colors"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Stats Overview */}
            <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
              <h2 className="text-lg font-semibold mb-4">Test Overview</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-gray-900 rounded-lg p-4 text-center border border-gray-700">
                  <div className="text-2xl font-bold text-blue-400">
                    {selectedQuestions.length}
                  </div>
                  <div className="text-sm text-gray-400">Questions</div>
                </div>
                <div className="bg-gray-900 rounded-lg p-4 text-center border border-gray-700">
                  <div className="text-2xl font-bold text-green-400">
                    {testData.totalMarks}
                  </div>
                  <div className="text-sm text-gray-400">Total Marks</div>
                </div>
                <div className="bg-gray-900 rounded-lg p-4 text-center border border-gray-700">
                  <div className="text-2xl font-bold text-yellow-400">
                    {testData.duration}
                  </div>
                  <div className="text-sm text-gray-400">Minutes</div>
                </div>
                <div className="bg-gray-900 rounded-lg p-4 text-center border border-gray-700">
                  <div className="text-2xl font-bold text-purple-400">
                    {testData.attemptsAllowed}
                  </div>
                  <div className="text-sm text-gray-400">Attempts</div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* Questions Tab */
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Available Questions Panel */}
            <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold">Available Questions</h3>
                  <p className="text-sm text-gray-400">
                    Select questions to add to your test
                  </p>
                </div>
                <span className="bg-blue-500/20 text-blue-400 px-3 py-1 rounded-full text-sm font-medium">
                  {availableQuestions.length} questions
                </span>
              </div>

              {/* Filters */}
              <div className="mb-4 space-y-3">
                <div className="flex items-center bg-gray-900 rounded-lg px-4 py-3 border border-gray-700 focus-within:border-blue-500 transition-colors">
                  <svg
                    className="w-5 h-5 text-gray-400 mr-3"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="flex-1 bg-transparent text-white focus:outline-none placeholder-gray-500"
                    placeholder="Search by question, topic, or subject..."
                  />
                </div>

                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => setSelectedSubject("")}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      !selectedSubject
                        ? "bg-blue-600 text-white"
                        : "bg-gray-700 text-gray-300 hover:bg-gray-600"
                    }`}
                  >
                    All Subjects
                  </button>
                  {subjects.map((subject) => (
                    <button
                      key={subject.id}
                      onClick={() => setSelectedSubject(subject.subject)}
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        selectedSubject === subject.subject
                          ? "bg-blue-600 text-white"
                          : "bg-gray-700 text-gray-300 hover:bg-gray-600"
                      }`}
                    >
                      {subject.subject}
                    </button>
                  ))}
                </div>
              </div>

              {/* Available Questions List */}
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {availableQuestions.map((question) => (
                  <div
                    key={question.id}
                    onClick={() => addQuestion(question)}
                    className="p-4 bg-gray-900 rounded-lg border border-gray-700 hover:border-blue-500 transition-colors cursor-pointer group"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="px-2 py-1 bg-gray-800 rounded text-xs font-medium">
                            {question.subject}
                          </span>
                          <span
                            className={`px-2 py-1 rounded text-xs font-medium ${
                              question.difficulty === "Easy"
                                ? "bg-green-500/20 text-green-400"
                                : question.difficulty === "Medium"
                                ? "bg-yellow-500/20 text-yellow-400"
                                : "bg-red-500/20 text-red-400"
                            }`}
                          >
                            {question.difficulty}
                          </span>
                        </div>
                        <p className="text-gray-200 text-sm line-clamp-2 mb-1">
                          {question.question}
                        </p>
                        {question.topic && (
                          <p className="text-xs text-gray-400">
                            Topic: {question.topic}
                          </p>
                        )}
                      </div>
                      <button className="ml-2 p-2 bg-blue-600 rounded text-white opacity-0 group-hover:opacity-100 transition-opacity">
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 4v16m8-8H4"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                ))}

                {availableQuestions.length === 0 && (
                  <div className="text-center py-8 text-gray-400">
                    <div className="text-2xl mb-2">🔍</div>
                    <div>No questions found matching your criteria</div>
                  </div>
                )}
              </div>
            </div>

            {/* Selected Questions Panel */}
            <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold">Selected Questions</h3>
                  <p className="text-sm text-gray-400">
                    Manage your test questions
                  </p>
                </div>
                <span className="bg-green-500/20 text-green-400 px-3 py-1 rounded-full text-sm font-medium">
                  {selectedQuestions.length} selected
                </span>
              </div>

              {/* Selected Questions List */}
              <div className="space-y-3 max-h-[507px] overflow-y-auto">
                {selectedQuestions.map((question, index) => (
                  <div
                    key={question.id}
                    className="p-4 bg-blue-500/10 rounded-lg border border-blue-500/30"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="px-2 py-1 bg-blue-600 rounded text-xs font-medium text-white">
                            #{index + 1}
                          </span>
                          <span className="px-2 py-1 bg-blue-500/20 rounded text-xs font-medium text-blue-400">
                            {question.subject}
                          </span>
                          <span
                            className={`px-2 py-1 rounded text-xs font-medium ${
                              question.difficulty === "Easy"
                                ? "bg-green-500/20 text-green-400"
                                : question.difficulty === "Medium"
                                ? "bg-yellow-500/20 text-yellow-400"
                                : "bg-red-500/20 text-red-400"
                            }`}
                          >
                            {question.difficulty}
                          </span>
                        </div>
                        <p className="text-gray-200 text-sm line-clamp-2 mb-1">
                          {question.question}
                        </p>
                        {question.topic && (
                          <p className="text-xs text-gray-400">
                            Topic: {question.topic}
                          </p>
                        )}
                      </div>
                      <div className="flex flex-col space-y-1 ml-2">
                        <button
                          onClick={() => moveQuestion(index, "up")}
                          disabled={index === 0}
                          className="p-1 bg-gray-700 rounded text-gray-300 hover:text-white hover:bg-gray-600 disabled:opacity-30 transition-colors"
                          title="Move up"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M5 15l7-7 7 7"
                            />
                          </svg>
                        </button>
                        <button
                          onClick={() => removeQuestion(question.id)}
                          className="p-1 bg-red-500/20 rounded text-red-400 hover:text-red-300 hover:bg-red-500/30 transition-colors"
                          title="Remove"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M6 18L18 6M6 6l12 12"
                            />
                          </svg>
                        </button>
                        <button
                          onClick={() => moveQuestion(index, "down")}
                          disabled={index === selectedQuestions.length - 1}
                          className="p-1 bg-gray-700 rounded text-gray-300 hover:text-white hover:bg-gray-600 disabled:opacity-30 transition-colors"
                          title="Move down"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 9l-7 7-7-7"
                            />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}

                {selectedQuestions.length === 0 && (
                  <div className="text-center py-8 text-gray-400">
                    <div className="text-2xl mb-2">📝</div>
                    <div>No questions selected yet</div>
                    <div className="text-sm mt-1">
                      Select questions from the left panel to build your test
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UpdateMockTestSeries;
